/* ═══════════════════════════════════════════════════════════════════════
   Formatter rules — ONE table (Spec Tighten D2, 2026-10-13).

   Every kind of thing the Formatter can find or rewrite in a document, with
   how sure it is and what happens by default. Three readers, one brain:
     - the engine (word-engine.ts) takes its report labels from here;
     - the review dialog's legend renders from here;
     - docs/FORMATTER_RULES.md is GENERATED from here
       (src/shared/formatterRules.test.ts fails on drift; run it with
       UPDATE_FORMATTER_RULES=1 to rewrite the doc).

   Law (docs/SPEC_WORKFLOW.md §13): the Formatter never deletes an author's
   paragraph on its own, and an unchecked facet leaves its zone
   byte-identical. Three categories (Spec Tighten R1–R5, 2026-10-13):
     - `certain` — the engine replacing its OWN earlier injections
       (bookmarked as ours). Never author content.
     - `facet-action` — a checked facet doing its own zone's job (Pre/Post
       replaces the title block and section end; Page setup sets the page;
       Headers / Footers write theirs). Each names its facet; an unchecked
       facet does none of it.
     - `judgment` — shown for review, defaults to Keep: only an explicit
       reviewer decision changes it.
   ══════════════════════════════════════════════════════════════════════ */

import type { DirtyItem, DirtyItemKind } from './forma';

/** Rows that are never an item in the review: the engine's own rewrites and
    the facets' zone actions. */
export type EngineRewriteKind =
  | 'previous-injection'
  | 'duplicate-part-label'
  | 'pre-body-replacement'
  | 'terminator-variant'
  | 'odd-even-break'
  | 'page-setup'
  | 'header-zone'
  | 'footer-zone';

export type FormatterRuleKind = DirtyItemKind | EngineRewriteKind;

export type FormatterRuleCategory = 'certain' | 'facet-action' | 'judgment';

/** The Formatter's facet checkboxes, by their engine key. */
export type FormatterFacet = 'headers' | 'footers' | 'prePost' | 'body' | 'pageSetup';

/** What happens when nobody decides: `keep` = the document is left exactly
    as written; `rewrite` = the engine replaces its own earlier output, or a
    checked facet does its zone's job. */
export type FormatterRuleVerb = 'keep' | 'rewrite';

export interface FormatterRule {
  kind: FormatterRuleKind;
  /** Plain words — what the review dialog and the report call it. */
  label: string;
  category: FormatterRuleCategory;
  /** `facet-action` rows only: the facet whose checkbox owns it. */
  facet?: FormatterFacet;
  defaultVerb: FormatterRuleVerb;
  /** One sentence, plain English. */
  whatHappens: string;
}

export const FORMATTER_RULES: readonly FormatterRule[] = [
  {
    kind: 'previous-injection',
    label: 'Earlier SpecForma zone',
    category: 'certain',
    defaultVerb: 'rewrite',
    whatHappens:
      'Pre-body, post-body and blank-page blocks an earlier SpecForma run inserted (bookmarked as ours) are replaced by this run’s.',
  },
  {
    kind: 'duplicate-part-label',
    label: 'Typed PART label on a PART heading',
    category: 'certain',
    defaultVerb: 'rewrite',
    whatHappens: 'The typed PART label duplicates the numbering the forma prints; it is removed.',
  },
  {
    kind: 'pre-body-replacement',
    label: 'Title block above the first PART line',
    category: 'facet-action',
    facet: 'prePost',
    defaultVerb: 'rewrite',
    whatHappens:
      'With Pre/Post checked, the paragraphs above the first PART line are replaced by the forma’s pre-body zone. Tables and pictures there stay. A document with no PART line (a form) has no pre-body zone and is left alone.',
  },
  {
    kind: 'terminator-variant',
    label: 'Section end',
    category: 'facet-action',
    facet: 'prePost',
    defaultVerb: 'rewrite',
    whatHappens:
      'With Pre/Post checked, the body’s last line — when it is exactly END OF SECTION / DOCUMENT / SPECIFICATION (or THIS PAGE INTENTIONALLY BLANK) — is replaced by the forma’s section end. Blank returns and page breaks around it stay; the same words anywhere else are shown for review.',
  },
  {
    kind: 'odd-even-break',
    label: 'Odd/even page section break',
    category: 'facet-action',
    facet: 'pageSetup',
    defaultVerb: 'rewrite',
    whatHappens:
      'With Page setup checked, an odd- or even-page section break is changed to a next-page break in place. The paragraph stays.',
  },
  {
    kind: 'page-setup',
    label: 'Page size and margins',
    category: 'facet-action',
    facet: 'pageSetup',
    defaultVerb: 'rewrite',
    whatHappens:
      'With Page setup checked, every section takes the forma’s page size and margins, top-aligned.',
  },
  {
    kind: 'header-zone',
    label: 'Header',
    category: 'facet-action',
    facet: 'headers',
    defaultVerb: 'rewrite',
    whatHappens: 'With Headers checked, the header is written from the forma.',
  },
  {
    kind: 'footer-zone',
    label: 'Footer',
    category: 'facet-action',
    facet: 'footers',
    defaultVerb: 'rewrite',
    whatHappens: 'With Footers checked, the footer is written from the forma.',
  },
  {
    kind: 'unheadered',
    label: 'Paragraph with no heading level',
    category: 'judgment',
    defaultVerb: 'keep',
    whatHappens:
      'Shown for review. Left as written unless you set a level or merge it into the previous paragraph.',
  },
  {
    kind: 'typed-list-marker',
    label: 'Hand-typed list marker',
    category: 'judgment',
    defaultVerb: 'keep',
    whatHappens:
      'A paragraph that starts with a typed marker like “A.” or “1.” Shown for review. Left as written unless you set a level (the typed marker is removed and the paragraph numbered) or flag it for editing.',
  },
  {
    kind: 'empty-paragraph',
    label: 'Blank return',
    category: 'judgment',
    defaultVerb: 'keep',
    whatHappens:
      'Shown for review. Kept unless you delete it — one by one or with “Delete all blank returns”.',
  },
  {
    kind: 'page-break',
    label: 'Manual page break',
    category: 'judgment',
    defaultVerb: 'keep',
    whatHappens:
      'Shown for review. Kept unless you delete it — one by one or with “Delete all page breaks”.',
  },
  {
    kind: 'stale-terminator',
    label: 'Nonstandard section end',
    category: 'judgment',
    defaultVerb: 'keep',
    whatHappens:
      'An END OF SECTION-style line that is not the body’s last line, or not in standard words. Shown for review. Left as written unless you replace it with the forma’s section end, or you choose Delete for that one line.',
  },
  {
    kind: 'pre-body-remnant',
    label: 'Leftover pre-body content',
    category: 'judgment',
    defaultVerb: 'keep',
    whatHappens:
      'Shown for review. Left as written unless you replace it with the forma’s section end, or you choose Delete for that one line.',
  },
  {
    kind: 'legacy-title-table',
    label: 'Title table above the body',
    category: 'judgment',
    defaultVerb: 'keep',
    whatHappens:
      'A “SECTION …” table above the first PART line. Shown for review. Kept unless you delete it (one inside an earlier SpecForma zone is replaced with that zone).',
  },
];

/** Every review-item kind, in the order the dialog lists them. */
export const REVIEW_KIND_ORDER: readonly DirtyItemKind[] = [
  'unheadered',
  'typed-list-marker',
  'empty-paragraph',
  'page-break',
  'stale-terminator',
  'pre-body-remnant',
  'legacy-title-table',
];
export function formatterRule(kind: FormatterRuleKind): FormatterRule {
  const rule = FORMATTER_RULES.find((r) => r.kind === kind);
  if (!rule) throw new Error(`No formatter rule for kind "${kind}"`);
  return rule;
}

/** What one review item reads as — the engine's report and the dialog both
    call this. A table-adjacent blank that ALSO carries a level renders as an
    empty numbered line next to a table, so it gets its own words. */
export function formatterItemLabel(item: {
  kind?: DirtyItemKind;
  tableAdjacent?: DirtyItem['tableAdjacent'];
  hasLevel?: boolean;
}): string {
  const kind = item.kind ?? 'unheadered';
  if (kind === 'empty-paragraph' && item.tableAdjacent && item.hasLevel) {
    return 'Numbered blank line by a table';
  }
  return formatterRule(kind).label;
}

/** Plain plural of a label for counts ("3 blank returns"). */
export function pluralLabel(label: string, n: number): string {
  const lower = label.charAt(0).toLowerCase() + label.slice(1);
  return n === 1 ? lower : `${lower}s`;
}

/** docs/FORMATTER_RULES.md — generated, never hand-edited. */
export function renderFormatterRulesMarkdown(): string {
  const facetName: Record<FormatterFacet, string> = {
    headers: 'Headers',
    footers: 'Footers',
    prePost: 'Pre/Post',
    body: 'Body',
    pageSetup: 'Page setup',
  };
  const row = (r: FormatterRule) =>
    `| \`${r.kind}\` | ${r.label} | ${r.defaultVerb === 'keep' ? 'Keep' : 'Rewrite'} | ${r.whatHappens} |`;
  const facetRow = (r: FormatterRule) =>
    `| \`${r.kind}\` | ${r.label} | ${r.facet ? facetName[r.facet] : ''} | ${r.whatHappens} |`;
  const of = (c: FormatterRule['category']) => FORMATTER_RULES.filter((r) => r.category === c);
  const header = ['| Kind | Label | Default | What happens |', '| --- | --- | --- | --- |'];
  const facetHeader = ['| Kind | Label | Facet | What happens |', '| --- | --- | --- | --- |'];
  return [
    '# Formatter rules',
    '',
    '> GENERATED from `src/shared/formatterRules.ts` — do not edit by hand.',
    '> Regenerate: `UPDATE_FORMATTER_RULES=1 npx vitest run src/shared/formatterRules.test.ts`.',
    '',
    'Law: the Formatter never deletes an author’s paragraph on its own',
    '(docs/SPEC_WORKFLOW.md §13). An unchecked facet leaves its zone byte-identical.',
    '',
    '## Certain — the engine rewrites its own earlier output',
    '',
    ...header,
    ...of('certain').map(row),
    '',
    '## Facet actions — a checked facet does its own zone',
    '',
    ...facetHeader,
    ...of('facet-action').map(facetRow),
    '',
    '## Judgment — shown for review, kept unless you decide otherwise',
    '',
    ...header,
    ...of('judgment').map(row),
    '',
  ].join('\n');
}

/** D8: what a body-off file says — in the written report AND on its row in
 *  the on-screen Results panel. Never "Clean": the body scan never ran. */
export const BODY_UNTOUCHED_LINE = 'Body untouched (Body unchecked)';

/** The note under a file's row in the Results panel, or null for none. */
export function formatResultNote(r: { success?: boolean; bodyUntouched?: boolean }): string | null {
  return r.success && r.bodyUntouched ? BODY_UNTOUCHED_LINE : null;
}
